import {
  Component, ChangeDetectionStrategy, Input, Output, EventEmitter,
  inject, OnInit, OnDestroy,
} from '@angular/core';
import { FikoYTKokBilesenComponent } from '@service/kok-bilesen/kok-bilesen.component';
import { CommonModule } from '@angular/common';
import { PdksDurumService } from '@uygulamalar/hmi-pano/services/pdks-durum.service';
import { PdksOfflineService } from '@uygulamalar/hmi-pano/services/pdks-offline.service';
import { FirmaService } from '@uygulamalar/firma-yonetim/services/firma.service';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { RouterModule } from '@angular/router';
import type { GirisCikisTur } from '@uygulamalar/firma-yonetim/interfaces/pdks.interface';

@Component({
  selector: 'pdks-aktif-kart',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, NzButtonModule, RouterModule],
  templateUrl: './pdks-aktif-kart.component.html',
  styleUrl: './pdks-aktif-kart.component.less',
})
export class PdksAktifKartComponent extends FikoYTKokBilesenComponent implements OnInit, OnDestroy {
  constructor() { super(); }
  pdks = inject(PdksDurumService);
  offline = inject(PdksOfflineService);
  private firmaService = inject(FirmaService);

  @Input() geceVardiyasi = false;
  @Output() durumDegisti = new EventEmitter<void>();

  islemYapiliyor = false;
  private readonly onlineDinleyici = () => this.bekleyenleriSenkronizeEt();

  ngOnInit() {
    window.addEventListener('online', this.onlineDinleyici);
    if (navigator.onLine) this.bekleyenleriSenkronizeEt();
  }

  override ngOnDestroy() {
    window.removeEventListener('online', this.onlineDinleyici);
    super.ngOnDestroy();
  }

  // Ring hesaplamaları
  private readonly CEVRE = 2 * Math.PI * 52; // ~326.7

  get tamamlanmaYuzde(): number {
    const gunluk = this.pdks.pdksAyarlar().gunlukCalismaSaati * 60;
    if (gunluk <= 0) return 0;
    return Math.min(100, Math.round((this.pdks.netCalismaDk() / gunluk) * 100));
  }

  get ringRenk(): string {
    const y = this.tamamlanmaYuzde;
    if (y >= 100) return '#fa8c16';
    if (this.pdks.aktifDurum() === 'molada') return '#fa8c16';
    return '#009688';
  }

  get ringDash(): string { return `${this.CEVRE}`; }
  get ringOffset(): string {
    return `${this.CEVRE * (1 - this.tamamlanmaYuzde / 100)}`;
  }

  get durumYazi(): string {
    switch (this.pdks.aktifDurum()) {
      case 'calisiyor': return 'Çalışıyor';
      case 'molada': return 'Molada';
      default: return 'Boşta';
    }
  }

  get normalStr(): string { return this.dk2Str(this.pdks.normalMesaiDk()); }
  get fazlaStr(): string { return this.dk2Str(this.pdks.fazlaMesaiDk()); }
  get toplamStr(): string { return this.dk2Str(this.pdks.toplamCalismaDk()); }
  get girisSaati(): string {
    const giris = this.pdks.gunlukKayitlar().find(k => !!k.giris)?.giris;
    return this.saatYaz(giris);
  }
  get cikisSaati(): string {
    const kayitlar = this.pdks.gunlukKayitlar();
    const cikis = [...kayitlar].reverse().find(k => !!k.cikis)?.cikis;
    return this.saatYaz(cikis);
  }
  get toplamMolaStr(): string { return this.dk2Str(this.pdks.toplamMolaDk()); }

  private saatYaz(zaman?: string): string {
    if (!zaman) return '--:--';
    return new Date(zaman).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  }

  private async yenile() { await this.pdks.gunlukVeriYukle(new Date()); }

  private async offlineKaydet(tur: GirisCikisTur) {
    const personel = this.pdks.aktifPersonel();
    if (!personel) throw new Error('Aktif personel bulunamadı');
    const zaman = new Date().toISOString();
    await this.offline.kayitEkle(this.pdks.firmaId(), personel._id, personel.kartUid || '', this.pdks.cihazId(), tur);
    this.pdks.yerelHareketEkle(tur, zaman);
  }

  private async bekleyenleriSenkronizeEt() {
    if (!navigator.onLine || this.offline.bekleyenSayisi() < 1) return;
    const sonuc = await this.offline.senkronizeEt(this.firmaService);
    if (sonuc.basarili > 0) await this.yenile();
  }

  private dk2Str(dk: number): string {
    if (dk <= 0) return '0dk';
    const h = Math.floor(dk / 60); const m = dk % 60;
    return h > 0 ? `${h}s ${m}dk` : `${m}dk`;
  }

  async girisYap() {
    this.islemYapiliyor = true;
    try {
      if (!navigator.onLine) {
        await this.offlineKaydet('giris');
        this.nzMessage.warning('Giriş çevrimdışı kuyruğa alındı');
      } else {
        const ok = await this.pdks.girisYap(this.firmaService);
        if (!ok) { this.nzMessage.warning('Çift kart okutma engeli — biraz bekleyin'); return; }
        this.nzMessage.success('Giriş kaydedildi');
      }
      this.durumDegisti.emit();
      if (navigator.onLine) await this.yenile();
    } catch { this.nzMessage.error('Giriş kaydedilemedi'); }
    finally { this.islemYapiliyor = false; }
  }

  async cikisYap() {
    this.islemYapiliyor = true;
    try {
      if (!navigator.onLine) {
        await this.offlineKaydet('cikis');
        this.nzMessage.warning('Çıkış çevrimdışı kuyruğa alındı');
      } else {
        const ok = await this.pdks.cikisYap(this.firmaService);
        if (!ok) return;
        this.nzMessage.success('Çıkış kaydedildi');
      }
      this.durumDegisti.emit();
      if (navigator.onLine) await this.yenile();
    } catch { this.nzMessage.error('Çıkış kaydedilemedi'); }
    finally { this.islemYapiliyor = false; }
  }

  async molaBaslat() {
    this.islemYapiliyor = true;
    try {
      if (!navigator.onLine) {
        await this.offlineKaydet('mola_baslangic');
        this.nzMessage.warning('Mola çevrimdışı kuyruğa alındı');
      } else {
        const ok = await this.pdks.molaBaslat_(this.firmaService);
        if (!ok) return;
        this.nzMessage.info('Mola başladı');
      }
      this.durumDegisti.emit();
      if (navigator.onLine) await this.yenile();
    } catch { this.nzMessage.error('Mola başlatılamadı'); }
    finally { this.islemYapiliyor = false; }
  }

  async molaBitir() {
    this.islemYapiliyor = true;
    try {
      if (!navigator.onLine) {
        await this.offlineKaydet('mola_bitis');
        this.nzMessage.warning('Mola bitişi çevrimdışı kuyruğa alındı');
      } else {
        const ok = await this.pdks.molaBitir_(this.firmaService);
        if (!ok) return;
        this.nzMessage.success('Mola bitti');
      }
      this.durumDegisti.emit();
      if (navigator.onLine) await this.yenile();
    } catch { this.nzMessage.error('Mola bitirilemedi'); }
    finally { this.islemYapiliyor = false; }
  }
}
